import React, { useEffect, useRef, useState } from 'react';
import { Filter } from 'lucide-react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import SearchBar from '../../../components/ui/SearchBar';
import AnchorDropdownPanel from '../../../components/ui/AnchorDropdownPanel';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { SectionSubtitle } from '../../../components/ui/PageLayout';
import SelectDropdown from '../../../components/ui/SelectDropdown';

export default function RepositoryActivityTab({
  repositoryId,
  tabActivity,
  tabActivityLoading,
  activityColumns,
  activityTypeOptions,
}: any) {
  const [activitySearch, setActivitySearch] = useState('');
  const [activityColumnFilters, setActivityColumnFilters] = useState<any[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [draftActivityType, setDraftActivityType] = useState('all');
  const filterAnchorRef = useRef<any>(null);

  useEffect(() => {
    setActivitySearch('');
    setActivityColumnFilters([]);
  }, [repositoryId]);

  useEffect(() => {
    if (!filterOpen) return;
    const cur = activityColumnFilters.find((f) => f.id === 'activityType')?.value ?? 'all';
    setDraftActivityType(typeof cur === 'string' ? cur : 'all');
  }, [filterOpen, activityColumnFilters]);

  const hasActiveActivityFilters = Boolean(
    activityColumnFilters.some(
      (f) => f.id === 'activityType' && f.value && String(f.value) !== 'all'
    )
  );

  const handleApplyFilters = () => {
    setActivityColumnFilters((prev) => {
      const rest = prev.filter((f) => f.id !== 'activityType');
      if (!draftActivityType || draftActivityType === 'all') return rest;
      return [...rest, { id: 'activityType', value: draftActivityType }];
    });
    setFilterOpen(false);
  };

  const handleClearAppliedFilters = () => {
    setActivityColumnFilters((prev) => prev.filter((f) => f.id !== 'activityType'));
    setDraftActivityType('all');
    setFilterOpen(false);
  };

  return (
    <div role="tabpanel" id="repo-tabpanel-activity" aria-labelledby="repo-tab-activity">
      <FeatureGate flag="ui.repositoryDetail.activityTab">
        <div className="card-mdl-static card-mdl-static-table overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <SectionSubtitle>Activity</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                Showing up to {tabActivity.length} loaded. Search applies to loaded rows; type
                filter applies on this page.
              </p>
            </div>
            {!tabActivityLoading && (
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:max-w-xl md:max-w-2xl">
                <div className="min-w-0 flex-1 sm:min-w-[12rem] sm:max-w-md">
                  <SearchBar
                    value={activitySearch}
                    onChange={setActivitySearch}
                    placeholder="Search activity…"
                  />
                </div>
                <button
                  ref={filterAnchorRef}
                  type="button"
                  className="btn-icon-mdl h-10 w-10 shrink-0 rounded-xl border border-dark-border"
                  onClick={() => setFilterOpen((v) => !v)}
                  aria-label="Filter activity by type"
                  title="Filter activity"
                  aria-expanded={filterOpen}
                  aria-haspopup="dialog"
                  aria-controls="repo-activity-filter-menu"
                >
                  <span className="relative inline-flex">
                    <Filter className="h-5 w-5 text-slate-200" aria-hidden />
                    {hasActiveActivityFilters && (
                      <span
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-vivid-red ring-dark-base"
                        aria-hidden
                      />
                    )}
                  </span>
                </button>
              </div>
            )}
          </div>
          <FilterableDataTable
            key={`activity-${repositoryId}`}
            data={tabActivity}
            columns={activityColumns}
            getRowId={(row) => String(row.id)}
            isLoading={tabActivityLoading}
            emptyMessage="No activity found."
            searchPlaceholder="Search activity…"
            initialSorting={[{ id: 'activity', desc: true }]}
            showColumnHeaders={false}
            globalFilter={activitySearch}
            onGlobalFilterChange={setActivitySearch}
            hideTopSearchRow
            columnFilters={activityColumnFilters}
            onColumnFiltersChange={setActivityColumnFilters}
          />

          <AnchorDropdownPanel
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterAnchorRef}
            id="repo-activity-filter-menu"
            title="Filter activity"
            titleIcon={<Filter className="h-5 w-5 shrink-0 text-vivid-red" aria-hidden />}
            panelWidth={320}
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="activity-type-filter"
                  className="block text-sm font-medium text-slate-300 mb-1.5"
                >
                  Activity type
                </label>
                <SelectDropdown
                  id="activity-type-filter"
                  className="w-full"
                  value={draftActivityType}
                  onChange={setDraftActivityType}
                  options={[
                    { value: 'all', label: 'All types' },
                    ...activityTypeOptions.map((t) => ({ value: t, label: t })),
                  ]}
                  compact
                />
              </div>
              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end sm:gap-3">
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-secondary-mdl-sm"
                  onClick={handleClearAppliedFilters}
                  disabled={
                    !hasActiveActivityFilters && (!draftActivityType || draftActivityType === 'all')
                  }
                >
                  Clear filters
                </button>
                <button type="button" className="btn-primary-mdl-sm" onClick={handleApplyFilters}>
                  Apply
                </button>
              </div>
            </div>
          </AnchorDropdownPanel>
        </div>
      </FeatureGate>
    </div>
  );
}
